import { useCallback, useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { showErrorToast } from '@/src/renderer/components/common/MessageToast'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/src/renderer/components/ui/Button'
import { Input } from '@/src/renderer/components/ui/Input'
import { PasswordInput } from '@/src/renderer/components/ui/PasswordInput'
import { ChevronLeft } from 'lucide-react'
import { InstantCameraIcon } from '@/src/renderer/components/icons/InstantCameraIcon'
import { ProfilePictureIcon } from '@/src/renderer/components/icons/ProfilePictureIcon'
import { FacePlusIcon } from '@/src/renderer/components/icons/FacePlusIcon'
import { getPlatformAPI } from '../../../lib/platform'
import { useAccountSource } from '../../../lib/accountSource'
import { buildOfflineUserProfile } from './buildOfflineUserProfile'
import { rememberDisplayName } from '@/src/renderer/lib/profile/rememberDisplayName'
import { suggestedDisplayName } from './firstNameFrom'
import { AvatarPickerPopover } from '@/src/renderer/components/common/AvatarPickerPopover'
import { navigateToHome } from '../../../routes/navigateToHome'
import './SetupProfilePage.scss'

/**
 * SetupProfilePage Component
 *
 * Profile setup page shown after successful OTP verification during registration.
 * Collects user's full name and password to complete their profile.
 *
 * @remarks
 * Features:
 * - Full name input with validation (required)
 * - Password input with minimum length validation (8 characters)
 * - Profile photo placeholder (future feature)
 * - Auto-focus on name input
 * - Enter key navigation between fields
 * - Updates Supabase user metadata
 * - Sets cloud-sync storage mode (Electron only)
 *
 * Validation Rules:
 * - Name: Cannot be empty
 * - Password: Minimum 8 characters
 *
 * Navigation Flow:
 * - Previous: OTP Verification (SetupPage)
 * - Next: Usage Questionary Page
 *
 * @example
 * ```tsx
 * <Route path="/setup-profile" element={
 *   <ProtectedRoute>
 *     <SetupProfilePage />
 *   </ProtectedRoute>
 * } />
 * ```
 */
export default function SetupProfilePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const platformAPI = getPlatformAPI()
  const account = useAccountSource()

  // Get saved data from navigation state (if coming back from purpose page)
  const locationState = location.state as {
    savedPurposes?: any[]
    savedIntents?: any[]
    profileData?: { fullName: string; password: string }
  }
  const savedPurposesFromState = locationState?.savedPurposes
  const savedIntentsFromState = locationState?.savedIntents
  const profileDataFromState = locationState?.profileData

  // Form States - Initialize with profileData from state if available
  const [fullName, setFullName] = useState(profileDataFromState?.fullName || '')
  const [password, setPassword] = useState(profileDataFromState?.password || '')
  const [nameError, setNameError] = useState(false)
  const [passwordError, setPasswordError] = useState(false)
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [initialName, setInitialName] = useState('') // Track initial value for change detection
  const [initialPassword, setInitialPassword] = useState('') // Track initial value for change detection
  const [isFirstTimeSetup, setIsFirstTimeSetup] = useState(false) // Track if this is first-time setup from OTP/offline flow

  // Avatar Picker States
  const [isAvatarPickerOpen, setIsAvatarPickerOpen] = useState(false)
  const [selectedAvatarId, setSelectedAvatarId] = useState<string | null>(null)
  const [selectedAvatarUrl, setSelectedAvatarUrl] = useState<string | null>(null)

  // Refs
  const nameInputRef = useRef<HTMLInputElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)

  // Check if offline mode - only read once on mount
  const [isOfflineMode] = useState(() => {
    const loginMethod = localStorage.getItem('loginMethod')
    return loginMethod === 'offline'
  })
  // OAuth (Google/Apple) users authenticated via the provider — no password to
  // set here, and the display name is a chosen nickname (NOT the provider name).
  const [isOAuth] = useState(() => localStorage.getItem('loginMethod') === 'oauth')
  // A user who already completed the local offline setup and is now connecting a
  // cloud account: purpose/intent/onboarding are already done locally, so the
  // wizard is short-circuited to /home after this profile step.
  const [connectingOfflineSetup] = useState(() => localStorage.getItem('offlineSetupCompleted') === 'true')
  // No password field for offline (no account) or OAuth (provider-authenticated).
  const hidePassword = isOfflineMode || isOAuth

  // Platform-spezifische CSS-Klasse
  const platformClass = platformAPI.platform === 'electron' ? 'setup-profile-page--electron' : 'setup-profile-page--web'

  /**
   * Initialization effect
   * Loads existing profile data if available, sets focus on name input
   */
  useEffect(() => {
    const loadProfile = async () => {
      // Track page view (only for cloud users)
      const loginMethod = localStorage.getItem('loginMethod')
      if (loginMethod !== 'offline') {
        await account.updateSetupProgress('profile')
      }

      // Check if this is first-time setup from OTP/offline flow
      const isFromSetupFlow = loginMethod === 'otp' || loginMethod === 'offline'
      setIsFirstTimeSetup(isFromSetupFlow)

      // If coming back with profileData from state, use that as initial values
      if (profileDataFromState) {
        setInitialName(profileDataFromState.fullName)
        setInitialPassword(profileDataFromState.password)
      } else {
        // Otherwise load from the account
        try {
          const user = await account.getCloudUser()
          // For OAuth the account name is the provider's full name ("Sven
          // Kaiser"), not a chosen nickname — so offer only the first part.
          // The field asks for a first name or nickname and stays editable;
          // pre-filling the full legal name would be wrong, offering nothing
          // makes every OAuth user type a name the provider already sent.
          let existingName = isOAuth
            ? suggestedDisplayName(user?.fullName, user?.providerName)
            : (user?.fullName ?? null)
          // Pre-fill from the offline profile when connecting an offline account
          // to a fresh cloud account (no cloud name yet), so the user doesn't have
          // to re-type the display name they already chose offline.
          if (!existingName) {
            const offlineProfile = await account.getOfflineProfile().catch(() => undefined)
            existingName = offlineProfile?.fullName ?? null
          }
          if (existingName) {
            setFullName(existingName)
            setInitialName(existingName)
          }
        } catch (error) {
          console.error('[SetupProfile] Error loading profile:', error)
        }
      }

      if (nameInputRef.current) {
        nameInputRef.current.focus()
      }
    }

    loadProfile()
    // Cleanup loginMethod flag only for OTP mode (nicht für offline mode!)
    // Im Offline-Modus muss der Flag erhalten bleiben für die Navigation
    const currentLoginMethod = localStorage.getItem('loginMethod')
    if (currentLoginMethod === 'otp') {
      localStorage.removeItem('loginMethod')
    }
    // Nur beim Betreten laden — ein erneuter Lauf überschriebe, was schon getippt wurde.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Handle avatar picker button click.
   * Memoized so the `React.memo`-wrapped {@link AvatarPickerPopover} doesn't
   * re-render on every keystroke in the name input.
   */
  const handleChangePhotoClick = useCallback(() => {
    setIsAvatarPickerOpen(true)
  }, [])

  /**
   * Handle avatar selection from picker
   */
  const handleSelectAvatar = useCallback(async (avatarId: string, imageUrl: string) => {
    setSelectedAvatarId(avatarId)
    setSelectedAvatarUrl(imageUrl)

    // Save avatar to database. On failure the optimistic UI keeps the
    // chosen avatar visible (we already setSelected... above) but the
    // toast tells the user the persistence step didn't land — matches
    // the AccountProfileSection pattern so behaviour is consistent
    // between setup and preferences.
    try {
      if (!isOfflineMode) {
        // Online mode: Save on the account
        const user = await account.getCloudUser()
        if (user) {
          await account.saveAvatarPreset(user.id, avatarId)
        }
      } else {
        // Offline mode: Save on the local profile
        await account.updateOfflineProfile({
          avatar_preset_id: avatarId,
        })
      }
    } catch (error) {
      console.error('[SetupProfile] Failed to save avatar:', error)
      showErrorToast(t('profile.saveFailed'))
    }
  }, [isOfflineMode, t, account])

  /**
   * Handles form submission
   * Validates inputs, updates user profile if changed, and navigates to purpose page
   */
  const handleContinue = async () => {
    if (isSubmitting) return
    // Validierung
    if (!fullName.trim()) {
      setNameError(true)
      nameInputRef.current?.focus()
      return
    }

    // A password is only required for email accounts — not offline (no account)
    // and not OAuth (provider-authenticated, no password field shown).
    if (!hidePassword) {
      if (!password || password.length < 10) {
        setPasswordError(true)
        passwordInputRef.current?.focus()
        return
      }
    }

    setNameError(false)
    setPasswordError(false)
    setIsSubmitting(true)

    // After the profile is saved, connecting offline users skip the rest of the
    // wizard (purpose/intent/onboarding already done locally) and go straight to
    // the app; everyone else continues to the purpose step.
    const goAfterProfile = async () => {
      if (connectingOfflineSetup) {
        await account.updateSetupProgress('completed').catch(() => {})
        localStorage.setItem('onboarding_completed', 'true')
        navigateToHome(navigate)
        return
      }
      navigate('/setup-purpose', {
        state: {
          savedPurposes: savedPurposesFromState,
          savedIntents: savedIntentsFromState,
          profileData: { fullName: fullName.trim(), password },
        },
      })
    }

    try {
      // Offline Mode: Save to Dexie
      if (isOfflineMode) {

        try {
          // Create or update offline user profile in Dexie. `put` replaces the
          // whole record, so the chosen avatar MUST be included here — otherwise
          // it gets wiped and Settings shows the fallback (see buildOfflineUserProfile).
          await account.putOfflineProfile(buildOfflineUserProfile(fullName.trim(), selectedAvatarId))

          // Also keep in localStorage for backward compatibility
          localStorage.setItem('offlineUserName', fullName.trim())
          rememberDisplayName(fullName)

        } catch (error) {
          console.error('[SetupProfile] Error saving to Dexie:', error)
          // Fallback to localStorage only
          localStorage.setItem('offlineUserName', fullName.trim())
          rememberDisplayName(fullName)
        }

        // Navigate to Purpose page
        navigate('/setup-purpose', {
          state: {
            savedPurposes: savedPurposesFromState,
            savedIntents: savedIntentsFromState,
            profileData: { fullName: fullName.trim(), password: '' }
          }
        })
        return
      }

      // Online Mode: Update database
      const nameChanged = fullName.trim() !== initialName
      // Only update password if:
      // 1. This is first-time setup from OTP/offline flow, OR
      // 2. Returning from navigation with profileData state
      // This prevents updating password when user has existing profile and autocomplete fills it
      const hasNavigationState = !!profileDataFromState
      const passwordChanged = password.length > 0 &&
                              password !== initialPassword &&
                              (isFirstTimeSetup || hasNavigationState)

      if (nameChanged || passwordChanged) {
        // Profil aktualisieren nur wenn sich etwas geändert hat
        const updateData: { fullName?: string; password?: string } = {}
        if (nameChanged) updateData.fullName = fullName.trim()
        if (passwordChanged) updateData.password = password

        const result = await account.saveProfile(updateData)

        if (result.success) {
          // Update initial values to prevent duplicate updates if user clicks again
          setInitialName(fullName.trim())
          setInitialPassword(password)

          // Session neu laden, damit user_metadata aktualisiert wird
          await account.refreshSession()

          // HappyCloud-Sync is a paid add-on — do NOT auto-enable it during
          // profile setup. storageMode stays 'offline' (local-only) until the
          // user activates sync in Settings.

          // Entferne loginMethod Flag (Profil-Setup abgeschlossen)
          localStorage.removeItem('loginMethod')

          // Setze profileSetupCompleted Flag
          localStorage.setItem('profileSetupCompleted', 'true')

          // Kurzer Delay, damit AuthProvider die Session aktualisieren kann
          await new Promise(resolve => setTimeout(resolve, 100))

          // Continue the wizard (or skip to /home for connecting offline users)
          await goAfterProfile()
        } else {
          // Fehler: Allgemeiner Update-Fehler
          console.error('[SetupProfile] Update error:', result.error)
          // TODO: Error-Message anzeigen
        }
      } else {
        // Keine Änderungen, direkt weiternavigieren (oder /home beim Verbinden)
        await goAfterProfile()
      }
    } catch (error) {
      console.error('[SetupProfile] Unexpected error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  /*
    ↵ schließt den Schritt ab — das Plättchen am Knopf verspricht es (gemeldet
    25.09.2026). Nach der Avatar-Auswahl liegt der Fokus nirgends mehr im
    Formular, das feldeigene onKeyDown greift dann nicht mehr. Am Fenster statt
    am Knopf, weil der Fokus nach der Auswahl auf keinem Element mehr steht.
    Name- und Passwortfeld behandeln Enter bereits selbst (Fokuswechsel bzw.
    Submit) — die sind hier bewusst ausgenommen, damit Enter dort nicht doppelt
    feuert. Während der Avatar-Picker offen ist, greift Enter nicht (Suchfeld
    tippen soll nicht das Formular abschicken).
  */
  const handleContinueRef = useRef(handleContinue)
  handleContinueRef.current = handleContinue
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.repeat || e.isComposing) return
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
      if (isAvatarPickerOpen || isSubmitting) return
      const target = e.target as HTMLElement | null
      if (target?.closest?.('input, textarea, select, button, a, [contenteditable="true"]')) return
      e.preventDefault()
      void handleContinueRef.current()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isAvatarPickerOpen, isSubmitting])

  return (
    <div className={`setup-profile-page ${platformClass}`}>
      {/* Scrollable Container */}
      <div className="setup-profile-page__scrollable-container">
        <div className="setup-profile-page__scrollable-inner">

          {/* Content Area */}
          <div className="setup-profile-page__content-area">
            {/* Left Side - Welcome */}
            <div className="setup-profile-page__left-section">
              <div className="setup-profile-page__left-content">
                <div className="setup-profile-page__icon">
                  <InstantCameraIcon />
                </div>
                <h1 className="setup-profile-page__title">
                  {t('setupProfile.title')}
                </h1>
                <p className="setup-profile-page__subtitle">
                  {isOfflineMode ? t('setupProfile.subtitleLocal') : t('setupProfile.subtitle')}
                </p>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="setup-profile-page__right-section">
              {/* Profile Photo */}
              <div className="setup-profile-page__photo-section">
                <div className="setup-profile-page__profile-photo">
                  <ProfilePictureIcon
                    selectedAvatarUrl={selectedAvatarUrl}
                  />
                </div>
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<FacePlusIcon />}
                    onClick={handleChangePhotoClick}
                  >
                    {t('setupProfile.changePhoto')}
                  </Button>
                  <AvatarPickerPopover
                    open={isAvatarPickerOpen}
                    onOpenChange={setIsAvatarPickerOpen}
                    onSelectAvatar={handleSelectAvatar}
                    selectedAvatarId={selectedAvatarId}
                    // Trigger sits in the right page section — open out to the
                    // left so the 374px popover stays inside the viewport.
                    align="right"
                  />
                </div>
              </div>

              {/* Form */}
              <div className="setup-profile-page__form">
                {/* Name Input */}
                <Input
                  ref={nameInputRef}
                  type="text"
                  name="name"
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value)
                    setNameError(false)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      if (isOfflineMode) {
                        // In offline mode, submit directly (no password field)
                        handleContinue()
                      } else {
                        // In online mode, focus next input (password)
                        passwordInputRef.current?.focus()
                      }
                    }
                  }}
                  placeholder={t('setupProfile.namePlaceholder')}
                  error={nameError}
                />
                {nameError && (
                  <p className="setup-profile-page__error-message">{t('setupProfile.nameRequired')}</p>
                )}

                {/* Password Input — only for email accounts (not offline, not OAuth) */}
                {!hidePassword && (
                  <div className="setup-profile-page__password-wrapper">
                    <PasswordInput
                      ref={passwordInputRef}
                      name="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value)
                        setPasswordError(false)
                      }}
                      onFocus={() => setIsPasswordFocused(true)}
                      onBlur={() => setIsPasswordFocused(false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleContinue()
                        }
                      }}
                      placeholder={t('setupProfile.passwordPlaceholder')}
                      error={passwordError}
                    />
                    {(isPasswordFocused || passwordError || password.length > 0) && (
                      <p className={`setup-profile-page__password-hint ${passwordError ? 'setup-profile-page__password-hint--error' : ''}`}>
                        {t('setupProfile.passwordHint')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Action Bar */}
          <div className="setup-profile-page__action-bar">
            <div className="setup-profile-page__action-bar-content">
              {/* Offline "Los geht's" path only: a way back to the WelcomeScreen
                  so the user isn't committed to local-only after one click. The
                  cloud (OTP) path reaches this page from the login screen and
                  has no WelcomeScreen behind it. */}
              {isOfflineMode && (
                <Button
                  variant="secondary"
                  className="setup-profile-page__back-to-welcome"
                  icon={<ChevronLeft size={16} aria-hidden />}
                  onClick={() => navigate('/')}
                >
                  {t('setup.back')}
                </Button>
              )}
              <Button
                onClick={handleContinue}
                onMouseDown={(e) => e.preventDefault()}
                shortcut={{ kind: 'return' }}
                // Kein `disabled` und kein „Speichern…“ während des kurzen Speicherns (#321): Beides blitzte vor
                // dem Wechsel zur Zweck-Seite auf. Den zweiten Klick fängt `handleContinue` ab.
              >
                {t('setupProfile.continue')}
              </Button>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}
